// Version Manager
// Coordinates between different version configurations

import { blocksV1 } from './v1/blocksV1';
import { blocksV2 } from './v2/blocksV2';

// Import assisting blocks for each version
import AssistingBlocksV1 from './v1/assistingBlocks';
import AssistingBlocksV2 from './v2/assistingBlocks';

// Symbol to indicate normalization should use latest version
const USE_LATEST = Symbol('USE_LATEST');
// Symbol to indicate normalization should use base version (for old agents)
const USE_BASE = Symbol('USE_BASE');

/**
 * Regex pattern to match version suffixes in block names (e.g., "_V2", "_V3")
 */
const VERSION_SUFFIX_PATTERN = /_V\d+$/;

/**
 * Unified version mapping - maps Snap versions to their complete configurations
 * Each version contains:
 *   - blocks: Toolbox block configurations (for ToolboxList)
 *   - assistingBlocks: Assisting block registration function
 *     (each version's assistingBlocks internally calls previous versions for backward compatibility)
 */
const versionConfigMap = {
    "2026.0.0": {
        blocks: blocksV1,
        assistingBlocks: AssistingBlocksV1,
    },
    "2026.1.0": {
        blocks: blocksV2,
        assistingBlocks: AssistingBlocksV2,  // Internally calls V1 first, then adds V2
    },
};

/**
 * Get blocks for a specific Snap version
 * - Old agents(Snap 2026.0.0) (undefined/null ServerBuildInfo): Falls back to BASE version (2026.0.0)
 * - New agents(Snap 2026.1.0+) with empty string (""): Falls back to LATEST version
 * - QA/development versions (0.0.0.x): Falls back to LATEST version
 * - Malformed versions: Falls back to LATEST version
 * - Unknown versions: Falls back to LATEST version
 * 
 * @param {string} snapVersion - The Snap version (e.g., "2026.1.0")
 * @returns {Object} Complete block configuration for all categories
 */
export function getBlocksForVersion(snapVersion) {
    const normalizedVersion = normalizeSnapVersion(snapVersion);
    
    // Handle old agents without ServerBuildInfo - use base version
    if (normalizedVersion === USE_BASE) {
        const baseVersion = getBaseVersion();
        console.log(`Old agent detected, using base version ${baseVersion}`);
        return versionConfigMap[baseVersion]?.blocks || {};
    }
    
    // Handle QA/malformed versions or version doesn't exist in map - use latest
    if (normalizedVersion === USE_LATEST || !versionConfigMap[normalizedVersion]) {
        const latestVersion = getLatestVersion();
        console.log(`Version ${snapVersion} not found, falling back to latest version ${latestVersion}`);
        return versionConfigMap[latestVersion]?.blocks || {};
    }
    
    // Valid version found - use exact match
    console.log(`Using Blockly version: ${normalizedVersion}`);
    return versionConfigMap[normalizedVersion].blocks;
}

/**
 * Normalize Snap version format
 * Only handles format normalization, not validity checking
 * 
 * @param {string} snapVersion - Raw version string
 * @returns {string|Symbol} Normalized version string, USE_LATEST symbol, or USE_BASE symbol
 */
function normalizeSnapVersion(snapVersion) {
    // Handle undefined/null (old agents without ServerBuildInfo)
    if (snapVersion === undefined || snapVersion === null) {
        console.log('No ServerBuildInfo provided (old agent), using base version');
        return USE_BASE;
    }
    
    // Handle empty string (agent sends ServerBuildInfo but it's empty)
    if (snapVersion.trim() === '') {
        console.log('Empty ServerBuildInfo provided, using latest version');
        return USE_LATEST;
    }
    
    // Handle QA environment versions (0.0.0.0, 0.0.0, etc.)
    if (snapVersion.startsWith('0.0.0')) {
        console.log(`QA version detected: ${snapVersion}, using latest version`);
        return USE_LATEST;
    }
    
    // Parse version (remove build number if present)
    const versionParts = snapVersion.split('.');
    if (versionParts.length >= 3) {
        return versionParts.slice(0, 3).join('.');
    }
    
    // Malformed version
    console.warn(`Malformed version: ${snapVersion}, using latest version`);
    return USE_LATEST;
}

/**
 * Get the latest (highest) version from the version map
 * 
 * @returns {string} Latest version string
 */
function getLatestVersion() {
    const versions = Object.keys(versionConfigMap).sort().reverse();
    return versions[0];
}

/**
 * Get the base (lowest) version from the version map
 * Used for old agents that don't provide ServerBuildInfo
 * 
 * @returns {string} Base version string
 */
function getBaseVersion() {
    const versions = Object.keys(versionConfigMap).sort();
    return versions[0];
}

/**
 * Get all blocks for backward compatibility
 * This ensures all block versions are registered with Blockly
 * 
 * @returns {Object} All blocks from all versions for registration
 */
export function getAllBlocksForBackwardCompatibility() {
    const allBlocks = {};
    
    // Merge all versions to get every block that ever existed
    for (const [version, versionConfig] of Object.entries(versionConfigMap)) {
        const blocksConfig = versionConfig.blocks;
        for (const [categoryName, blocks] of Object.entries(blocksConfig)) {
            if (!allBlocks[categoryName]) {
                allBlocks[categoryName] = {};
            }
            
            // Add blocks only if they don't already exist (avoid overwrites)
            // This ensures we collect all unique block versions for backward compatibility
            for (const [blockName, blockConfig] of Object.entries(blocks)) {
                if (!allBlocks[categoryName][blockName]) {
                    // Block doesn't exist yet, add it entirely
                    allBlocks[categoryName][blockName] = blockConfig;
                } else if (blockConfig.childComponents) {
                    // Block exists but we need to merge childComponents
                    // This handles categories like "Widget Values" where blocks have nested childComponents
                    if (!allBlocks[categoryName][blockName].childComponents) {
                        allBlocks[categoryName][blockName].childComponents = {};
                    }
                    
                    // Merge childComponents from this version
                    for (const [childName, childConfig] of Object.entries(blockConfig.childComponents)) {
                        if (!allBlocks[categoryName][blockName].childComponents[childName]) {
                            allBlocks[categoryName][blockName].childComponents[childName] = childConfig;
                        }
                    }
                }
            }
        }
    }
    
    return allBlocks;
}

/**
 * Get all available versions
 * 
 * @returns {string[]} Array of available version strings
 */
export function getAvailableVersions() {
    return Object.keys(versionConfigMap);
}

/**
 * Register assisting blocks for backward compatibility
 * 
 * Calls the latest version's assisting blocks, which internally chains to all previous versions.
 * (e.g., V3 → V2 → V1, so 'rest_params', 'rest_params_v2', 'rest_params_v3' are all registered)
 * 
 * This ensures saved scripts from any version can be loaded without errors.
 * 
 * @param {Object} props - Props to pass to assisting blocks (typically { context })
 */
export function registerAssistingBlocks(props) {
    const latestVersion = getLatestVersion();
    const versionConfig = versionConfigMap[latestVersion];
    
    if (versionConfig?.assistingBlocks) {
        console.log(`Registering assisting blocks (latest version: ${latestVersion})`);
        versionConfig.assistingBlocks(props);
    } else {
        console.warn(`No assisting blocks found for version ${latestVersion}`);
    }
}

/**
 * Check if a version exists
 * 
 * @param {string} snapVersion - The version to check
 * @returns {boolean} True if version exists
 */
export function isVersionSupported(snapVersion) {
    return snapVersion in versionConfigMap;
}

/**
 * Get the correct versioned block name for the current Snap version
 * 
 * This function looks up the block in the version config for the user's Snap version
 * and returns the actual blockName from the component.
 * 
 * The version configs (blocksV1, blocksV2, etc.) tell us which version to use
 * Read the component's blockName property from the version config.
 * 
 * 
 * @param {string} baseBlockName - The BASE block name WITHOUT version suffix (e.g., "rest_service")
 * @param {string} snapVersion - The Snap version (e.g., "2026.1.0")
 * @returns {string} The versioned block name to use (e.g., "rest_service_V2")
 */
export function getVersionedBlockName(baseBlockName, snapVersion) {

    // Check if developer passed a versioned name by mistake, if so, strip the version suffix
    if (VERSION_SUFFIX_PATTERN.test(baseBlockName)) {
        const strippedName = baseBlockName.replace(VERSION_SUFFIX_PATTERN, '');
        baseBlockName = strippedName;
    }
    
    // Helper function to check if a component matches the base block name
    const checkComponentMatch = (component) => {
        if (component && component.blockName) {
            const componentBlockName = component.blockName;
            // Check if this component's blockName matches our base pattern
            if (componentBlockName === baseBlockName || 
                componentBlockName.startsWith(baseBlockName + '_V')) {
                console.log(`Resolved block "${baseBlockName}" for version ${snapVersion} → "${componentBlockName}"`);
                return componentBlockName;
            }
        }
        return null;
    };
    
    // Get the appropriate blocks config for this version
    const blocksConfig = getBlocksForVersion(snapVersion);
    
    // Search through all categories to find the block
    for (const [categoryName, categoryBlocks] of Object.entries(blocksConfig)) {
        for (const [blockKey, blockConfig] of Object.entries(categoryBlocks)) {
            // Check main component
            const mainMatch = checkComponentMatch(blockConfig.component);
            if (mainMatch) {
                return mainMatch;
            }
            
            // Also check child components
            if (blockConfig.childComponents) {
                for (const childConfig of Object.values(blockConfig.childComponents)) {
                    const childMatch = checkComponentMatch(childConfig.component);
                    if (childMatch) {
                        return childMatch;
                    }
                }
            }
        }
    }
    

    // If the block doesn't exist in the version config, it means either:
    // 1. The block name has a typo (developer error)
    // 2. The block doesn't exist in this Snap version yet
    // Returning the base name will let Blockly fail with "block type not found" error
    // which is the correct behavior - we should NOT reslove blocks from future versions!
    console.log(`Block "${baseBlockName}" not found in version ${snapVersion}, using base name`);
    return baseBlockName;
}